import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/cn";
import {
  TODAY_DENSITIES,
  TODAY_DENSITY_LABELS,
  TODAY_PAGE_SIZES,
  type TodayListDensity,
  type TodayPageSize,
} from "@/lib/today-list-prefs";

export function TodayListControls({
  page,
  pageSize,
  totalItems,
  density,
  onPageChange,
  onPageSizeChange,
  onDensityChange,
}: {
  page: number;
  pageSize: TodayPageSize;
  totalItems: number;
  density: TodayListDensity;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: TodayPageSize) => void;
  onDensityChange: (density: TodayListDensity) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-1.5">
          <span className="font-medium text-foreground">Show</span>
          <select
            className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground"
            value={pageSize}
            onChange={(e) =>
              onPageSizeChange(Number(e.target.value) as TodayPageSize)
            }
            aria-label="Items per page"
          >
            {TODAY_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <div
          className="inline-flex items-center rounded-md border border-border bg-card p-0.5"
          role="group"
          aria-label="List density"
        >
          {TODAY_DENSITIES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={density === option}
              onClick={() => onDensityChange(option)}
              className={cn(
                "rounded px-2 py-1 font-medium transition",
                density === option
                  ? "bg-brand-soft text-brand"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {TODAY_DENSITY_LABELS[option]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="tabular-nums">
          {from}–{to} of {totalItems}
        </span>
        <button
          type="button"
          aria-label="Previous page"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className="inline-flex size-7 items-center justify-center rounded-md border border-border text-foreground hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="size-3.5" />
        </button>
        <span className="min-w-14 text-center tabular-nums text-foreground">
          {safePage} / {totalPages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          className="inline-flex size-7 items-center justify-center rounded-md border border-border text-foreground hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
